import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { replace } from '@avangard/utils/array';
import {
    Button,
    DatepickerSinglePicker,
    DatepickerTextField,
    OnDateChangeProps,
    TextField,
} from '@avangard/ui/core';
import { MenuItem } from '@material-ui/core';
import { useEnqueueStacks } from '@avangard/utils/hooks';

import { LapPlatoonMask } from '@modules/layout/atoms';
import { FormStyled } from '@modules/layout/styled';
import { ExtendedFormik, Form } from '@modules/layout/organisms';
import { PLATOONS_COMPANY_OPTIONS, programTypes } from '@modules/lap/constants';
import { UpdateLapRequest } from '@modules/lap/requests';
import { UpdateLapMutation, GetLapCollectionQuery, GetLapsQuery } from '@modules/lap/graphql';

import type { LapEntity } from '@modules/lap/entities';
import type { UpdateLapFormValues } from '@modules/lap/requests';
import type {
    LapPlatoonsItem,
    UpdateLapMutationType,
    UpdateLapMutationVariables,
} from '@modules/types/graphql';

type LapEditFormProps = {
    canEdit: boolean;
    lap: LapEntity;
    onCancel: () => void;
};

const StyledForm = styled(Form)`
    width: 100%;
    max-height: 600px;

    > * {
        &:not(:last-of-type) {
            margin-bottom: 24px;
        }
    }
`;

const FormRowSubmit = styled(FormStyled.FormRowSubmit)`
    justify-content: flex-end;
`;

const platoonInputProps = {
    inputComponent: LapPlatoonMask,
};

const LapEditForm = (props: LapEditFormProps) => {
    const { canEdit, lap, onCancel } = props;

    const { t } = useTranslation(['common', 'errors', 'lap']);

    const { enqueueSuccess, enqueueError } = useEnqueueStacks();

    const [updateLap] = useMutation<UpdateLapMutationType, UpdateLapMutationVariables>(
        UpdateLapMutation,
    );

    const groupedPlatoonsByCompany = React.useMemo(() => lap.getGroupedPlatoonsByCompany(), [lap]);

    const initialValues: UpdateLapFormValues = React.useMemo(() => {
        const platoonsByCompanies = Object.entries(groupedPlatoonsByCompany).reduce(
            (carry, [company, platoons]) => {
                carry.push([Number(company), String(platoons.length)]);

                return carry;
            },
            [] as [number, string][],
        );

        return {
            programType: lap.programType ?? '',
            from: lap.getDate(lap.from),
            to: lap.getDate(lap.to),

            platoonsCompanyCount: lap.maxCompany,
            platoonsByCompanies,
        };
    }, [groupedPlatoonsByCompany]);

    return (
        <ExtendedFormik<UpdateLapFormValues>
            validationSchema={yup.object({
                programType: yup.string().required(t('errors:validations.required') ?? ''),
                from: yup
                    .date()
                    .nullable()
                    .required(t('errors:validations.required') ?? ''),
                to: yup
                    .date()
                    .nullable()
                    .required(t('errors:validations.required') ?? '')
                    .min(yup.ref('from'), t('errors:time.invalid_range') ?? ''),
            })}
            initialValues={initialValues}
            onSubmit={async values => {
                try {
                    const item = new UpdateLapRequest(values);

                    const { data: updateLapData } = await updateLap({
                        variables: { id: lap.id, item },
                        refetchQueries: [{ query: GetLapCollectionQuery }, { query: GetLapsQuery }],
                        awaitRefetchQueries: true,
                    });

                    const lapId = updateLapData?.updateLap?.id;

                    if (lapId) {
                        enqueueSuccess(t('lap:notifiers.edit_lap.success'));
                        onCancel();
                    } else {
                        enqueueError(t('lap:notifiers.edit_lap.error'));
                    }
                } catch (e) {
                    console.log(e);
                    throw e;
                }
            }}
        >
            {formikProps => {
                const { values, errors, isSubmitting, setFieldValue, handleChange } = formikProps;

                const handleChangeDate = (field: string) => (data: OnDateChangeProps) =>
                    setFieldValue(field, data.date);

                const handleChangePlatoonsCompanyCount = (
                    event: React.ChangeEvent<HTMLInputElement>,
                ) => {
                    const companyCount = +event.target.value;

                    setFieldValue('platoonsCompanyCount', companyCount);

                    let updatedPlatoonsByCompanies = [...values.platoonsByCompanies];

                    if (companyCount > updatedPlatoonsByCompanies.length) {
                        for (let i = updatedPlatoonsByCompanies.length; i < companyCount; i++) {
                            updatedPlatoonsByCompanies.push([i + 1, '']);
                        }
                    } else if (companyCount < updatedPlatoonsByCompanies.length) {
                        updatedPlatoonsByCompanies = updatedPlatoonsByCompanies.slice(
                            0,
                            companyCount,
                        );
                    }

                    setFieldValue('platoonsByCompanies', updatedPlatoonsByCompanies);
                };

                const handleChangePlatoonsByCompaniesItem =
                    (company: number, index: number) =>
                    (event: React.ChangeEvent<HTMLInputElement>) => {
                        const platoonCount = event.target.value;

                        setFieldValue(
                            'platoonsByCompanies',
                            replace(values.platoonsByCompanies, [company, platoonCount], index),
                        );
                    };

                return (
                    <StyledForm>
                        <DatepickerSinglePicker
                            disableToday
                            date={values.from}
                            onDateChange={handleChangeDate('from')}
                        >
                            {datepickerProps => {
                                const { inputProps, inputComponent } = datepickerProps;

                                return (
                                    <DatepickerTextField
                                        id='from'
                                        label={t('common:forms.labels.start_lap_date')}
                                        placeholder={t('common:forms.placeholder.date') ?? ''}
                                        disabled={!canEdit}
                                        error={!!errors.from}
                                        helperText={errors.from}
                                        InputProps={{ inputComponent }}
                                        {...inputProps}
                                    />
                                );
                            }}
                        </DatepickerSinglePicker>

                        <DatepickerSinglePicker
                            disableToday
                            date={values.to}
                            onDateChange={handleChangeDate('to')}
                        >
                            {datepickerProps => {
                                const { inputProps, inputComponent } = datepickerProps;

                                return (
                                    <DatepickerTextField
                                        id='to'
                                        label={t('common:forms.labels.end_lap_date')}
                                        placeholder={t('common:forms.placeholder.date') ?? ''}
                                        disabled={!canEdit}
                                        error={!!errors.to}
                                        helperText={errors.to}
                                        InputProps={{ inputComponent }}
                                        {...inputProps}
                                    />
                                );
                            }}
                        </DatepickerSinglePicker>

                        <TextField
                            select
                            fullWidth
                            id='programType'
                            name='programType'
                            label={t('common:forms.labels.program_type')}
                            placeholder={t('common:forms.placeholder.choose_program_type') ?? ''}
                            disabled={!canEdit}
                            value={values.programType}
                            error={!!errors.programType}
                            helperText={errors.programType}
                            onChange={handleChange}
                        >
                            <MenuItem disabled value=''>
                                {t('common:forms.placeholder.choose_program_type')}
                            </MenuItem>

                            {programTypes.map(programType => (
                                <MenuItem key={programType.key} value={programType.key}>
                                    {t(programType.value)}
                                </MenuItem>
                            ))}
                        </TextField>

                        {lap.isDynamicPlatoons ? (
                            <>
                                <TextField
                                    select
                                    fullWidth
                                    id='platoonsCompanyCount'
                                    name='platoonsCompanyCount'
                                    label={t('lap:form.placeholder.typing_company_count')}
                                    placeholder={
                                        t('lap:form.placeholder.typing_company_count') ?? ''
                                    }
                                    value={values.platoonsCompanyCount}
                                    onChange={handleChangePlatoonsCompanyCount}
                                >
                                    <MenuItem value=''>{t('common:stubs.not_chosen')}</MenuItem>

                                    {PLATOONS_COMPANY_OPTIONS.map(companyOption => (
                                        <MenuItem
                                            key={companyOption}
                                            value={companyOption}
                                            disabled={companyOption < lap.maxCompany}
                                        >
                                            {companyOption}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {values.platoonsByCompanies.map(([company, platoon], idx) => {
                                    const currentPlatoon = groupedPlatoonsByCompany[company] as
                                        | LapPlatoonsItem[]
                                        | undefined;

                                    const inputProps = {
                                        min: currentPlatoon ? currentPlatoon.length : 1,
                                        max: 8,
                                    };

                                    return (
                                        <TextField
                                            required
                                            fullWidth
                                            type='number'
                                            id='platoonsCompanyCount'
                                            name='platoonsCompanyCount'
                                            key={company}
                                            label={t(
                                                'lap:form.placeholder.typing_platoon_count_in_company_count',
                                                { company },
                                            )}
                                            placeholder={
                                                t(
                                                    'lap:form.placeholder.typing_platoon_count_in_company',
                                                ) ?? ''
                                            }
                                            value={platoon}
                                            inputProps={inputProps}
                                            InputProps={platoonInputProps}
                                            onChange={handleChangePlatoonsByCompaniesItem(
                                                company,
                                                idx,
                                            )}
                                        />
                                    );
                                })}
                            </>
                        ) : null}

                        <FormRowSubmit>
                            <Button variant='outlined' disabled={isSubmitting} onClick={onCancel}>
                                {t('common:actions.cancel')}
                            </Button>

                            {canEdit ? (
                                <Button type='submit' loading={isSubmitting}>
                                    {t('common:actions.force_change')}
                                </Button>
                            ) : null}
                        </FormRowSubmit>
                    </StyledForm>
                );
            }}
        </ExtendedFormik>
    );
};

export { LapEditForm };
